import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useMutation } from "@tanstack/react-query";
import { imagineStore } from "@/stores/imagine.store";
import { Loader2 } from "lucide-react";
import { EnvironmentVariablesDialogTrigger } from "./environment-variables/environment-variables-dialog";
import { useState } from "react";
import {
  ArrowPathIcon,
  VariableIcon,
  PencilSquareIcon,
} from "@heroicons/react/16/solid";
import { cn } from "@/lib/utils";
import { RenameProjectDialog } from "@/components/studio/rename-project-dialog";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { toast } from "sonner";

export function AdvancedActions({
  children,
  disabled,
}: {
  children: React.ReactNode;
  disabled: boolean;
}) {
  const projectId = imagineStore.use.projectId();
  const project = imagineStore.use.project();
  const [isFetchingVariables, setIsFetchingVariables] = useState(false);
  const [open, setOpen] = useState(false);
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const restartSandboxMutation = useMutation({
    mutationFn: async () => {
      const response = await aiServiceFetch(
        `/api/projects/${projectId}/restart`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      return response.json();
    },
    onSuccess: () => {
      window.location.reload();
    },
  });

  const disableAll = restartSandboxMutation.isPending || isFetchingVariables;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          disabled={disableAll}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            restartSandboxMutation.mutate();
          }}
        >
          <div className="flex items-center gap-2">
            <ArrowPathIcon
              className={cn(
                "size-4 inline",
                restartSandboxMutation.isPending && "animate-spin",
              )}
            />
            Restart Sandbox
          </div>
        </DropdownMenuItem>
        <EnvironmentVariablesDialogTrigger
          onFetchingVariablesChange={setIsFetchingVariables}
          onOpenSuccess={() => {
            void trackAnalyticsEvent("project.variables_dialog_opened", {
              projectId: projectId ?? undefined,
            });
          }}
        >
          <DropdownMenuItem
            disabled={!sandboxInstrumented}
            onSelect={(e) => {
              e.preventDefault();
            }}
          >
            <div className="flex items-center gap-2">
              {isFetchingVariables ? (
                <Loader2 className="size-4 inline animate-spin" />
              ) : (
                <VariableIcon className="size-4 inline" />
              )}
              Variables
            </div>
          </DropdownMenuItem>
        </EnvironmentVariablesDialogTrigger>
        {projectId && project?.name && (
          <RenameProjectDialog
            projectId={projectId}
            currentName={project.name}
            onOpenChange={(dialogOpen) => {
              if (!dialogOpen) {
                setOpen(false);
              }
            }}
          >
            <DropdownMenuItem
              disabled={disableAll}
              onSelect={(e) => e.preventDefault()}
            >
              <div className="flex items-center gap-2">
                <PencilSquareIcon className="size-4 inline" />
                Rename Project
              </div>
            </DropdownMenuItem>
          </RenameProjectDialog>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
